/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AlertProps } from '@mui/material';
import { addTagToUserThunk } from '../settings/settingsSlice';
import type { RootState } from '../../reducers';

type DrawerState = 'open' | 'closed';

interface AlertMessage {
  message: string;
  type: AlertProps['severity'];
  buttonText?: string;
  link?: string;
  // Add more properties if needed
}

type NotificationType =
  | 'transcription'
  | 'processing'
  | 'error'
  | 'tag'
  | 'team'
  | 'import';
interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  link?: string;
  read: boolean;
  timestamp: number;
}

interface DashboardState {
  drawerState: DrawerState;
  alertMessageStack: AlertMessage[]; // Alert message shown on the top of the dashboard
  notifications: Notification[];
}

// Helper function to save read notification to tags
const saveReadNotificationToTags = (notification: Notification) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      const state = getState();
      const existingHistory =
        state.settings.user.tags['read-notifications-history'] || '[]';

      let readHistory: Array<{
        id: string;
        type: NotificationType;
        title: string;
        readAt: number;
      }> = [];
      try {
        readHistory = JSON.parse(existingHistory as string);
      } catch {
        readHistory = [];
      }

      // Add this notification to read history (avoid duplicates)
      const exists = readHistory.some((item) => item.id === notification.id);
      if (!exists) {
        readHistory.push({
          id: notification.id,
          type: notification.type,
          title: notification.title,
          readAt: Date.now(),
        });

        // Keep only last 50 read notifications to avoid too much data
        if (readHistory.length > 50) {
          readHistory = readHistory.slice(-50);
        }

        // Save back to tags
        dispatch(
          addTagToUserThunk({
            key: 'read-notifications-history',
            value: JSON.stringify(readHistory),
          })
        );
      }
    } catch (error) {
      console.error('Failed to save read notification to tags:', error);
    }
  };
};

// Thunk action to mark notification as read and save to history
export const markNotificationAsReadWithHistory = createAsyncThunk(
  'dashboard/markNotificationAsReadWithHistory',
  async (notificationId: string, { dispatch, getState }) => {
    const state = getState() as RootState;
    const notification = state.dashboard.notifications.find(
      (n: Notification) => n.id === notificationId
    );

    if (notification && !notification.read) {
      // Save to read history
      await dispatch(saveReadNotificationToTags(notification));
    }

    return notificationId;
  }
);

// Thunk action to clear all notifications and save to history
export const clearAllNotificationsWithHistory = createAsyncThunk(
  'dashboard/clearAllNotificationsWithHistory',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const unreadNotifications = state.dashboard.notifications.filter(
      (n: Notification) => !n.read
    );

    // Save all unread notifications to history
    await Promise.all(
      unreadNotifications.map((notification: Notification) =>
        dispatch(saveReadNotificationToTags(notification))
      )
    );

    return true;
  }
);

const initialState: DashboardState = {
  drawerState: 'open',
  alertMessageStack: [],
  notifications: [
    // {
    //   id: '1',
    //   type: 'transcription',
    //   title: 'File Processed',
    //   message: 'File "test.pdf" has been processed',
    //   link: 'https://www.google.com',
    //   read: false,
    //   timestamp: Date.now(),
    // },
  ],
};

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setDrawerState: (state, action: PayloadAction<DrawerState>) => {
      state.drawerState = action.payload;
    },
    setAlertMessage: (state, action: PayloadAction<AlertMessage>) => {
      state.alertMessageStack.push({
        buttonText: 'Dismiss',
        ...action.payload,
      });
    },
    removeAlertMessage: (state) => {
      state.alertMessageStack.shift();
    },
    setNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.notifications = action.payload;
    },
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.push(action.payload);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (n) => n.id !== action.payload
      );
    },
    updateNotification: (state, action: PayloadAction<Notification>) => {
      const index = state.notifications.findIndex(
        (n) => n.id === action.payload.id
      );
      if (index !== -1) {
        state.notifications[index] = action.payload;
      } else {
        // If notification doesn't exist, add it
        state.notifications.push(action.payload);
      }
    },
    markNotificationAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(
        (n) => n.id === action.payload
      );
      if (notification) {
        notification.read = true;
      }
    },
    clearAllNotifications: (state) => {
      state.notifications = state.notifications.map((notification) => ({
        ...notification,
        read: true,
      }));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(markNotificationAsReadWithHistory.fulfilled, (state, action) => {
        const notification = state.notifications.find(
          (n) => n.id === action.payload
        );
        if (notification) {
          notification.read = true;
        }
      })
      .addCase(clearAllNotificationsWithHistory.fulfilled, (state) => {
        state.notifications = state.notifications.map((notification) => ({
          ...notification,
          read: true,
        }));
      });
  },
});

export const {
  setDrawerState,
  setAlertMessage,
  removeAlertMessage,
  setNotifications,
  addNotification,
  removeNotification,
  updateNotification,
  markNotificationAsRead,
  clearAllNotifications,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
